import React, {Component} from "react"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"

import Form from "@irati/ui/src/Form"
import authentication from "@irati/auth"
import File from "@irati/ui/src/FormItem/File"
import Input from "@irati/ui/src/FormItem/Input"
import Select from "@irati/ui/src/FormItem/Select"
import {Checkbox} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import {Para} from "@irati/ui/src/Labels"
import Link from "@irati/ui/src/Link"
import Button from "@irati/ui/src/Button"
import {H3, H4, H5, Body} from "@irati/ui/src/Typography"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import Hr from "@irati/ui/src/Layout/Hr"
import {createSamlIdentityProvider, getProviderByTenantReference} from "@irati/sso/services/sso"
import {initiateSamlLogin} from "@irati/sso/helpers/webSSOPopup"
import businessService from "@irati/identity/services/business"
import Loading from "@irati/ui/src/Loading"
import {UpdateBusinessInput} from "@irati/types/ops"
import {
  SAML_REQUEST_TYPES,
  SAML_REQUEST_TYPE_POST,
  RESOLVED_SAML_REQUEST_TYPES_KEY
} from "@irati/sso/constants/signUpOptions"
import adminTracking from "@irati-tracking/admin"
import {Tenant} from "@irati/types"
import {store} from "@irati/store"
import AuthActions from "@irati/auth/store/actions/AuthActions"

import {hangPromise} from "@irati/utils/promise"
import SAMLKeyValue from "./SAMLKeyValue"

interface Props extends InjectIntl {
  userTenant: Tenant,
  ssoButtonText: string,
  handleSave: (value: string) => void,
  hideDocHint?: boolean
}

interface State {
  SAMLRequestType: string | null,
  requiresAuthNRequest: boolean,
  SAMLRequestName: string | null,
  SAMLRequestIssuer: string | null,
  hideNormalLogin: boolean,
  load: Promise<void> | null,
  providerMetadata: string | null,
  saving: Promise<void> | null,
  saved: boolean,
  showSamlInformation: boolean,
  uuid: string | null,
  ssoButtonText: string,
  isTestingSSO: Promise<void> | null
}

class SAMLSingleSignOn extends Component<Props, State> {
  state: State = {
    SAMLRequestType: null,
    requiresAuthNRequest: false,
    SAMLRequestName: null,
    SAMLRequestIssuer: null,
    hideNormalLogin: false,
    load: hangPromise(),
    providerMetadata: null,
    saving: null,
    saved: false,
    showSamlInformation: false,
    uuid: null,
    ssoButtonText: this.props.ssoButtonText,
    isTestingSSO: null
  }

  componentDidMount() {
    this.setState({
      load: getProviderByTenantReference()
        .then((res) => {
          const SAMLRequestIssuer = res?.data?.config?.saml?.SAMLRequestIssuer
          const SAMLRequestName = res?.data?.config?.saml?.SAMLRequestName
          this.setState({
            SAMLRequestType: res?.data?.config?.saml?.SAMLRequestType,
            requiresAuthNRequest: !!(SAMLRequestName || SAMLRequestIssuer),
            SAMLRequestName,
            SAMLRequestIssuer
          })
        })
        .catch((err) => {
          if (err.response.status !== 404) {
            throw new Error(err)
          }
        })
    })
  }

  getSAMLRequestTypes = () => {
    const {formatMessage} = this.props
    return RESOLVED_SAML_REQUEST_TYPES_KEY.map((type) => ({value: type.value, label: formatMessage({id: type.label})}))
  }

  onChange = (label: string, value: any) => {
    if (label === "requiresAuthNRequest" && this.state.SAMLRequestType !== SAML_REQUEST_TYPE_POST) {
      this.setState((state) => ({
        requiresAuthNRequest: !state.requiresAuthNRequest
      }))
    } else {
      this.setState({[label]: value})
    }
  }

  updateTenantSettings = () => {
    const {userTenant, handleSave, formatMessage} = this.props
    const {ssoButtonText, hideNormalLogin} = this.state

    const data: UpdateBusinessInput = {
      uuid: userTenant.uuid,
      loginPageSsoButtonText: ssoButtonText,
      showPasswordLogin: !hideNormalLogin,
      showSsoLogin: true
    }
    const isTestingSSO = businessService.update({data}).then(() => {
      return store.dispatch(AuthActions.updateTenant()).then(() => {
        this.setState({isTestingSSO: null})
        handleSave(formatMessage({id: "admin.components.samlSingleSignOn.updateTenantSettings.setUp"}))
        adminTracking.enableSSO()
      })
    })

    this.setState({
      isTestingSSO
    })
  }

  saveSaml = () => {
    const {providerMetadata, SAMLRequestType, SAMLRequestName, SAMLRequestIssuer, requiresAuthNRequest} = this.state
    const formData = new FormData()
    formData.append("metadata-file", new Blob([providerMetadata], {type: "application/xml"}))
    formData.append("tenant", this.props.userTenant.uuid)
    formData.append("saml_request_type", SAMLRequestType)

    if (requiresAuthNRequest || SAMLRequestType === SAML_REQUEST_TYPE_POST) {
      formData.append("saml_request_name", SAMLRequestName)
      formData.append("saml_request_issuer", SAMLRequestIssuer)
    }
    const saving = createSamlIdentityProvider(formData).then((response) => {
      this.setState({
        uuid: response.data.uuid,
        saved: true,
        saving: null,
        showSamlInformation: true
      })
    })
    this.setState({saving})
  }

  testSSO = () => {
    initiateSamlLogin(authentication.userTenant()).then(this.updateTenantSettings)
  }

  editSaml = () => {
    this.setState({
      showSamlInformation: false
    })
  }

  render() {
    const {hideDocHint, formatMessage, userTenant} = this.props
    const {
      showSamlInformation,
      load,
      ssoButtonText,
      providerMetadata,
      SAMLRequestIssuer,
      SAMLRequestName,
      SAMLRequestType,
      hideNormalLogin,
      requiresAuthNRequest,
      saving,
      saved,
      uuid,
      isTestingSSO
    } = this.state

    const ssoDocsLink = (
      <Link href={formatMessage({id: "admin.components.samlSingleSignOn.link.href"})} rel="noopener noreferrer" target="_blank">
        <FormattedMessage id="admin.components.samlSingleSignOn.link.text" />
      </Link>
    )

    return (
      <Loading load={load}>
        {!hideDocHint && (
          <Body>
            <FormattedMessage id="admin.components.samlSingleSignOn.viewDocumentation.textV2" values={{link: ssoDocsLink}} />
          </Body>
        )}
        {!userTenant.settings.showSSOOptions && (
          <H3>
            <FormattedMessage id="admin.components.samlSingleSignOn.headerThree.contact" />
          </H3>
        )}

        <H3 marginTop={2} marginBottom={2}>
          <FormattedMessage id="admin.components.samlSingleSignOn.header.stepOne" />
        </H3>
        {showSamlInformation ? (
          <Button className="pull-right" onClick={this.editSaml}>
            <FormattedMessage id="admin.components.samlSingleSignOn.button.edit" />
          </Button>
        ) : (
          <Form>
            <Input
              name="ssoButtonText"
              label={formatMessage({id: "admin.components.samlSingleSignOn.input.label.loginButton.text"})}
              placeholder={formatMessage({id: "admin.components.samlSingleSignOn.input.placeholder.singleSignOn.text"})}
              value={ssoButtonText}
              hint={formatMessage({id: "admin.components.samlSingleSignOn.input.hint.singleSignOn.text"})}
              type="text"
              onChange={(value) => this.onChange("ssoButtonText", value)}
              validation={Validation.string(formatMessage({id: "admin.components.samlSingleSignOn.input.validation.required"})).max(
                50,
                formatMessage({id: "admin.components.samlSingleSignOn.input.validation.maxCharacters"})
              )} />
            <File
              label={formatMessage({id: "admin.components.samlSingleSignOn.file.label"})}
              type=".xml"
              name="providerMetadata"
              file={providerMetadata}
              onChange={(file) => {
                this.onChange("providerMetadata", file[0])
              }}
              validation={Validation.string(formatMessage({id: "admin.components.samlSingleSignOn.file.validation.required"}))} />
            <Select
              name="SAMLRequestType"
              label={formatMessage({id: "admin.components.samlSingleSignOn.select.label"})}
              value={SAMLRequestType}
              isClearable={false}
              options={this.getSAMLRequestTypes()}
              onChange={(value) => this.onChange("SAMLRequestType", value)}
              validation={Validation.string(formatMessage({id: "admin.components.samlSingleSignOn.select.validation.required"})).oneOf(
                SAML_REQUEST_TYPES,
                formatMessage({id: "admin.components.samlSingleSignOn.select.validation.oneOf"})
              )} />
            <Checkbox
              name="hideNormalLogin"
              checked={hideNormalLogin}
              onChange={() => this.onChange("hideNormalLogin", !hideNormalLogin)}>
              <FormattedMessage id="admin.components.samlSingleSignOn.hideNormalLogin.text" />
            </Checkbox>
            <Checkbox
              name="requiresAuthNRequest"
              checked={requiresAuthNRequest || SAMLRequestType === SAML_REQUEST_TYPE_POST}
              onChange={() => this.onChange("requiresAuthNRequest")}>
              <FormattedMessage id="admin.components.samlSingleSignOn.requiresAuthNRequest.text" />
            </Checkbox>
            {(requiresAuthNRequest || SAMLRequestType === SAML_REQUEST_TYPE_POST) && (
              <>
                <Input
                  name="SAMLRequestName"
                  label={formatMessage({id: "admin.components.samlSingleSignOn.samlRequestName.input.label"})}
                  placeholder={formatMessage({id: "admin.components.samlSingleSignOn.samlRequestName.input.placeholder"})}
                  value={SAMLRequestName}
                  type="text"
                  onChange={(value) => this.onChange("SAMLRequestName", value)}
                  validation={Validation.string(
                    formatMessage({id: "admin.components.samlSingleSignOn.samlRequestName.input.validation.required"})
                  )
                    .max(50)
                    .matches(
                      /^[a-z0-9_-]*$/i,
                      formatMessage({id: "admin.components.samlSingleSignOn.samlRequestName.input.validation.matches"})
                    )} />
                <Input
                  name="SAMLRequestIssuer"
                  label={formatMessage({id: "admin.components.samlSingleSignOn.samlRequestIssuer.input.label"})}
                  placeholder={formatMessage({id: "admin.components.samlSingleSignOn.samlRequestIssuer.input.placeholder"})}
                  value={SAMLRequestIssuer}
                  onChange={(value) => this.onChange("SAMLRequestIssuer", value)}
                  validation={Validation.string(
                    formatMessage({id: "admin.components.samlSingleSignOn.samlRequestIssuer.input.validation.required"})
                  )} />
              </>
            )}
            <ValidationButton loading={saving} primary onClick={this.saveSaml} style={{marginTop: 15}}>
              <FormattedMessage id="admin.components.samlSingleSignOn.validationButton.continue" />
            </ValidationButton>
          </Form>
        )}

        {saved && (
          <>
            <Hr />
            <H3 style={{marginBottom: 15}}>
              <FormattedMessage id="admin.components.samlSingleSignOn.header.stepTwo" />
            </H3>
            <H4>
              <FormattedMessage id="admin.components.samlSingleSignOn.headerFour.information" />
            </H4>
            <H4 small>
              <FormattedMessage id="admin.components.samlSingleSignOn.headerFour.dynamicAssertions" />
            </H4>
            <H5>
              <FormattedMessage id="admin.components.samlSingleSignOn.headerFive.userUniqueId" />
            </H5>
            <p>
              <FormattedMessage id="admin.components.samlSingleSignOn.saml.format.text" />
            </p>
            <SAMLKeyValue value={uuid} namekey="https://sso.perkbox.co.uk/SAML/Attributes/Tenant/UUID" />
            <Para>
              <FormattedMessage id="admin.components.samlSingleSignOn.headerFour.staticAssertions" />
              &nbsp;
              {ssoDocsLink}
            </Para>
            <Button loading={isTestingSSO} primary onClick={this.testSSO} style={{marginTop: 15}}>
              <FormattedMessage id="admin.components.samlSingleSignOn.button.testAndSave" />
            </Button>
          </>
        )}
      </Loading>
    )
  }
}

export default injectIntl(SAMLSingleSignOn)
